import React, { useCallback, useState } from 'react';
import { Card, Typography, Grid, Box } from '@mui/material';
import {
  Cell,
  Label,
  Pie,
  PieChart,
  ResponsiveContainer,
  Sector,
} from 'recharts';
import { ErrorOutline } from '@mui/icons-material';
import { useCleverTapPageTracking } from '../../../utils/clevertap';

const NetworkSales = ({ networkSalesReport }) => {
  // CleverTap screen tracking
  useCleverTapPageTracking('Network Sales Chart', 'performance');

  const [activeIndex, setActiveIndex] = useState(-1);

  const network_sales_report = networkSalesReport;
  const rawData =
    network_sales_report?.chart_info?.map((item) => ({
      value: item?.quantity,
      color: item?.color,
      label: item?.network_name,
    })) || [];

  const data = rawData.filter((item) => item.value > 0);

  const totalValue = data.reduce((sum, item) => sum + item.value, 0);

  const onPieEnter = useCallback((_, index) => {
    setActiveIndex(index);
  }, [data]);

  const onPieLeave = useCallback(() => {
    setActiveIndex(-1);
  }, []);

  const renderActiveShape = (props) => {
    const RADIAN = Math.PI / 180;
    const {
      cx,
      cy,
      midAngle,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      fill,
      payload,
      percent,
      value,
    } = props;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const sx = cx + (outerRadius + 5) * cos;
    const sy = cy + (outerRadius + 5) * sin;
    const mx = cx + (outerRadius + 15) * cos;
    const my = cy + (outerRadius + 15) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 11;
    const ey = my;
    const textAnchor = cos >= 0 ? 'start' : 'end';

    return (
      <g>
        <text
          x='50%'
          y='45%'
          textAnchor='middle'
          dy={8}
          fill='#000'
          fontSize={16}
          fontWeight='bold'
        >
          {payload.label}
        </text>
        <text
          x='50%'
          y='55%'
          textAnchor='middle'
          dy={8}
          fill='#000'
          fontSize={14}
          fontWeight='bold'
        >
          {`${network_sales_report?.chart_info[0].prefix_uom || ''}${value} ${
            network_sales_report?.chart_info[0].suffix_uom || ''
          }`}
        </text>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={outerRadius + 3}
          outerRadius={outerRadius + 5}
          fill={fill}
        />
      </g>
    );
  };

  return (
    <Card sx={{ p: 2, borderRadius: 2, height: '100%' }}>
      <Grid
        container
        alignItems='center'
        justifyContent='space-between'
      >
        <Typography sx={{ fontSize: 18, fontWeight: 600 }}>
          {network_sales_report?.section_header_display_title}
        </Typography>
        <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
          {network_sales_report?.month_name}
        </Typography>
      </Grid>

      {data.length > 0 ? (
        <Box
          display={'flex'}
          flexDirection={{ xs: 'column', md: 'row' }}
          justifyContent={'space-between'}
          alignItems={'center'}
        >
          <Box
            sx={{
              width: '100%',
              height: 200,
            }}
          >
            <ResponsiveContainer
              width='100%'
              height='100%'
            >
              <PieChart>
                <Pie
                  onMouseEnter={onPieEnter}
                  onMouseLeave={onPieLeave}
                  activeIndex={activeIndex}
                  activeShape={renderActiveShape}
                  data={data}
                  cx='50%'
                  cy='50%'
                  labelLine={false}
                  innerRadius={55}
                  outerRadius={70}
                  fill='#8884d8'
                  dataKey='value'
                  nameKey='label'
                  paddingAngle={2}
                  cornerRadius={5}
                >
                  {data.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.color}
                    />
                  ))}
                  {activeIndex === -1 && (
                    <Label
                      content={({ viewBox }) => {
                        const { cx, cy } = viewBox;
                        return (
                          <>
                            <text
                              x={cx}
                              y={cy - 10}
                              textAnchor='middle'
                              dominantBaseline='central'
                              fontSize={14}
                              fontWeight='bold'
                              fill='#333'
                            >
                              Total Sales
                            </text>
                            <text
                              x={cx}
                              y={cy + 10}
                              textAnchor='middle'
                              dominantBaseline='central'
                              fontSize={16}
                              fontWeight='bold'
                              fill='#333'
                            >
                              {`${
                                network_sales_report?.chart_info[0]
                                  .prefix_uom || ''
                              }${totalValue.toFixed(2)}${
                                network_sales_report?.chart_info[0]
                                  .suffix_uom || ''
                              }`}
                            </text>
                          </>
                        );
                      }}
                    />
                  )}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </Box>

          <Box sx={{ width: '100%' }}>
            {network_sales_report?.chart_legends_info.map((legend) => (
              <Box
                key={legend.display_title}
                display='flex'
                alignItems='center'
                sx={{
                  mb: 1,
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: legend.color,
                    marginRight: 1,
                  }}
                />
                <Box
                  sx={{
                    display: 'flex',

                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                >
                  <Typography sx={{ fontSize: 12, fontWeight: '600' }}>
                    {legend.display_title}
                  </Typography>
                  <Typography
                    variant='body2'
                    fontWeight={500}
                    color='textSecondary'
                    sx={{ fontSize: 12 }}
                  >
                    {legend.display_value}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      ) : (
        <Grid
          container
          justifyContent='center'
          alignItems='center'
          sx={{ height: '100%', minHeight: 200 }}
        >
          <Box textAlign='center'>
            <ErrorOutline sx={{ fontSize: 60, color: '#B2B5C4' }} />
            <Typography
              sx={{ fontSize: 16, fontWeight: 600, color: '#8E92A8', mt: 1 }}
            >
              No Data Found
            </Typography>
          </Box>
        </Grid>
      )}
    </Card>
  );
};

export default NetworkSales;
